import apiClient from './index';
import csvToDataFrame from './csvParser';

// Queries API endpoints
const queriesApi = {
  // Execute a synchronous query
  executeSync: async (query, mode = 'adql', file = null, tableName = null, format = 'parquet') => {
    const formData = new FormData();
    formData.append('query', query);
    formData.append('mode', mode);
    formData.append('format', format);
    
    if (file) {
      formData.append('file', file);
      if (tableName) {
        formData.append('table_name', tableName);
      } else {
        throw new Error('Table name is required when uploading a file');
      }
    }
    
    try {
      console.log(`Executing sync query: "${query.substring(0, 100)}${query.length > 100 ? '...' : ''}" (mode: ${mode}, format: ${format})`);
      const response = await apiClient.post('/adss/sync', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: format === 'csv' ? 'text' : 'arraybuffer'
      });
      
      console.log('Sync query response status:', response.status);
      
      // Get execution metadata from headers
      const executionTime = response.headers['x-execution-time-ms'] || 0;
      const rowCount = response.headers['x-row-count'] || 0;
      
      // Process based on format
      let data;
      if (format === 'csv') {
        // Process CSV text data
        console.log('Processing CSV data');
        data = csvToDataFrame(response.data);
      } else {
        // For Parquet, provide only the download URL but don't parse
        // Just add placeholder data with a direct format indicator
        
        data = {
          columns: ["Download"],
          rows: [{ "Download": `${format} file is available for download` }],
          rowCount: 1,
          columnCount: 1,
          data: response.data,  // Store the raw data for download
          isParquetDownload: true      // Flag to indicate this is a parquet download
        };
      }
      
      return {
        ...data,
        mode,
        format,
        executionTime: parseInt(executionTime, 10),
        rowCount: parseInt(rowCount, 10) || data.rowCount
      };
    } catch (error) {
      console.error('Sync query error:', error);
      
      // Handle case where the server redirected to async
      // Check if the response contains a job ID
      if (error.response && error.response.data) {
        try {
          // Try to parse the response as JSON if possible
          const textDecoder = new TextDecoder('utf-8');
          let text;
          
          if (error.response.data instanceof ArrayBuffer) {
            text = textDecoder.decode(error.response.data);
          } else {
            text = error.response.data;
          }
          
          const jsonResponse = JSON.parse(text);
          
          if (jsonResponse.id) {
            console.log('Server redirected to async mode:', jsonResponse.id);
            // This query was executed as async
            return {
              isAsync: true,
              jobId: jsonResponse.id,
              mode,
              format,
              message: 'Query was too complex for sync execution and was submitted as async'
            };
          }
        } catch (parseError) {
          // Not JSON, continue with normal error handling
        }
      }
      
      // Re-throw the original error
      throw error;
    }
  },
  
  // Start an asynchronous query
  executeAsync: async (query, mode = 'adql', file = null, tableName = null, format = 'parquet') => {
    const formData = new FormData();
    formData.append('query', query);
    formData.append('mode', mode);
    formData.append('format', format);
    
    if (file) {
      formData.append('file', file);
      if (tableName) {
        formData.append('table_name', tableName);
      } else {
        throw new Error('Table name is required when uploading a file');
      }
    }
    
    console.log(`Executing async query: "${query.substring(0, 100)}${query.length > 100 ? '...' : ''}" (mode: ${mode}, format: ${format})`);
    const response = await apiClient.post('/adss/async', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    
    console.log('Async query response:', response.data);
    
    if (!response.data || !response.data.id) {
      throw new Error('Invalid response: Missing job ID for async query');
    }
    
    return {
      ...response.data,
      mode,
      format
    };
  },
  
  // Check status of an asynchronous query
  getQueryStatus: async (queryId) => {
    console.log(`Checking status for query ${queryId}`);
    const response = await apiClient.get(`/adss/async/${queryId}`);
    console.log(`Status for query ${queryId}:`, response.data);
    return response.data;
  },
  
  // Get results of a completed asynchronous query
  // Get results of a completed asynchronous query
  getQueryResults: async (queryId) => {
    try {
      console.log(`Fetching results for async query ${queryId}...`);

      // 1) Still use axios for status (interceptors, auth error handling)
      const statusResponse = await apiClient.get(`/adss/async/${queryId}`);
      const queryFormat = statusResponse.data.format || 'parquet';

      // 2) Stream only the first 5 MB of the results using fetch()
      const LIMIT = 5 * 1024 * 1024; // 5 MB
      const url = `/adss/async/${queryId}/results`;

      // Carry over auth/cookies manually since fetch doesn't use axios interceptors
      const token = localStorage.getItem('token');
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': queryFormat === 'csv' ? 'text/csv' : 'application/octet-stream',
        },
        credentials: 'include', // send cookies like axios withCredentials
      });

      if (res.status === 404) {
        return {
          error: true,
          message: 'Query results have expired or are no longer available',
          resultsExpired: true,
          columns: ['Error'],
          rows: [{ Error: 'Results no longer available' }],
          format: queryFormat,
          mode: statusResponse.data.mode || 'adql',
          jobId: queryId,
        };
      }

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      // 3) Read up to LIMIT bytes and then abort
      const reader = res.body?.getReader?.();
      let received = 0;
      const chunks = [];

      if (reader) {
        while (received < LIMIT) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.byteLength;
          if (received >= LIMIT) {
            try { reader.cancel(); } catch {}
            break;
          }
        }
      } else {
        // No streaming (very old browser) — fall back to full fetch then slice
        const ab = await res.arrayBuffer();
        chunks.push(new Uint8Array(ab.slice(0, LIMIT)));
        received = Math.min(LIMIT, ab.byteLength);
      }

      // Concatenate & precisely trim to LIMIT
      const blob = new Blob(chunks);
      let ab = await blob.arrayBuffer();
      if (ab.byteLength > LIMIT) ab = ab.slice(0, LIMIT);

      // 4) Build the response object depending on format
      const expiresAt = res.headers.get('x-expires-at');
      const mode = statusResponse.data.mode || 'adql';

      if (queryFormat === 'csv') {
        // Decode and trim to last newline to avoid partial row at the end
        let text = new TextDecoder('utf-8').decode(ab);
        const lastNL = Math.max(text.lastIndexOf('\n'), text.lastIndexOf('\r'));
        if (lastNL > 0) text = text.slice(0, lastNL);

        if (!text || !text.trim()) {
          throw new Error('No data returned from server for async query (empty CSV).');
        }

        const df = csvToDataFrame(text);
        return {
          ...df,
          format: 'csv',
          mode,
          expiresAt,
          jobId: queryId,
          isPartial: received >= LIMIT, // let UI know this is partial preview
          bytesFetched: received,
          maxBytes: LIMIT,
        };
      }

      // Non-CSV (e.g., parquet): we DO NOT parse. We only fetched a partial preview.
      // Expose a friendly row saying it's a partial binary preview.
      return {
        columns: ['Download'],
        rows: [{ Download: `${queryFormat.toUpperCase()} preview (first 5 MB) fetched; full file available for download.` }],
        rowCount: 1,
        columnCount: 1,
        data: ab,                // partial bytes buffer
        isParquetDownload: true, // keep your flag so UI treats it as a download artifact
        isPartial: true,         // IMPORTANT: partial, not a complete file
        bytesFetched: received,
        maxBytes: LIMIT,
        format: queryFormat,
        mode,
        expiresAt,
        jobId: queryId,
      };

    } catch (error) {
      console.error(`Error fetching async query results for ${queryId}:`, error);

      return {
        error: true,
        message: `Failed to load async query results: ${error.message}`,
        statusCode: error.response?.status,
        details: error.response?.data?.detail || error.message,
        columns: ['Error'],
        rows: [{ Error: `Failed to load results: ${error.message}` }],
        rowCount: 1,
        columnCount: 1,
      };
    }
  },
  
  // Cancel an asynchronous query
  cancelQuery: async (queryId) => {
    const response = await apiClient.delete(`/adss/async/${queryId}`);
    return response.data;
  },
  
  // Get queue status
  getQueueStatus: async () => {
    const response = await apiClient.get('/queue-status');
    return response.data;
  },
  
  // Get query history
  getQueryHistory: async (limit = 50) => {
    const response = await apiClient.get('/adss/v1/queries/me', {
      params: { limit }
    });
    
    // Debug log to see response data structure
    console.log('Query history API response data:', response.data);
    console.log('Query history API response headers:', response.headers);
    
    // Check if we need to fix missing fields
    if (response.data && Array.isArray(response.data)) {
      response.data.forEach(query => {
        // Fix missing is_async field
        if (query.is_async === undefined) {
          // Set is_async based on job_id or result_url containing "async"
          const hasJobId = !!query.id;
          const hasAsyncUrl = query.result_url && query.result_url.includes('/async/');
          query.is_async = hasJobId || hasAsyncUrl;
          
          console.log(`Fixed missing is_async for query ${query.id}: set to ${query.is_async}`);
        }
        
        // Fix missing format field (default to parquet)
        if (query.format === undefined || query.format === null) {
          query.format = 'parquet';
          console.log(`Fixed missing format for query ${query.id}: set to parquet`);
        }
        
        // Fix missing mode field (default to adql)
        if (query.mode === undefined || query.mode === null) {
          query.mode = 'adql';
          console.log(`Fixed missing mode for query ${query.id}: set to adql`);
        }
      });
    }
    
    return response.data;
  },
  
  // Get query details by ID
  getQueryDetails: async (queryId) => {
    const response = await apiClient.get(`/adss/v1/queries/${queryId}`);
    
    // Fix missing fields for query details too
    const query = response.data;
    if (query) {
      // Fix missing is_async field
      if (query.is_async === undefined) {
        const hasJobId = !!query.job_id;
        const hasAsyncUrl = query.result_url && query.result_url.includes('/async/');
        query.is_async = hasJobId || hasAsyncUrl;
      }
      
      // Fix missing format field
      if (query.format === undefined || query.format === null) {
        query.format = 'parquet';
      }
      
      // Fix missing mode field
      if (query.mode === undefined || query.mode === null) {
        query.mode = 'adql';
      }
    }
    
    return response.data;
  },
  
  // Delete a query from history
  deleteQueryFromHistory: async (queryId) => {
    const response = await apiClient.delete(`/adss/v1/queries/${queryId}`);
    return response.data;
  },
};

export default queriesApi;